<template>
  <div class="form-group"
       ref="multiselect"
       :class="{ hasErrors }">
    <label class="form-label"
           :class="formLabelClass"
           v-if="label"
           :data-content="label">
      <span>{{ label }}</span>
      <span v-if="required"> *</span>
    </label>
    <div v-if="!editMode">
      <div v-if="!resultsBelow" class="word-break">{{ stringValue }}</div>
      <div v-if="resultsBelow" class="d-flex flex-wrap gap-2">
        <div class="service-multiselect-below-tag word-break"
             v-for="(o, oind) in belowTags"
             :key="`below-tag-${oind}`">
          {{ o[title] }}
        </div>
      </div>

    </div>
    <div v-if="editMode">
      <VueMultiselect :options="options"
                      :searchable="true"
                      v-model="f"
                      :class="{ textLikeInput, 'last-tag-locked': lastTagLocked }"
                      :label="title"
                      :placeholder="$t(placeholder)"
                      selectLabel=""
                      selectedLabel=""
                      deselectLabel=""
                      :internalSearch="internalSearch"
                      :track-by="title"
                      @remove="remove"
                      :allow-empty="allowEmpty"
                      :disabled="readonly"
                      @select="select"
                      :group-select="false"
                      :[groupLabel]="'category'"
                      :[groupValues]="'services'"
                      @open="serviceFind"
                      @search-change="serviceFind"
                      ref="multi"
                      :multiple="multiple">
<!--        <template #placeholder><div class="badge badge-info">asdfasdf</div></template>-->
        <template #selection v-if="resultsBelow"><span class="multiselect__placeholder">{{ belowTags.length ? $t(placeholder) : '' }}</span></template>
        <template #singleLabel>
          <div v-html="customSingleLabel ? customSingleLabel(f) : f[title]"/>
        </template>
        <template #option="{ option }"
                  v-if="customOptionsLabel">
          <span v-html="customOptionsLabel(option)"></span>
        </template>
        <template #option="{ option }"
                  v-else-if="$slots.option">
          <slot name="option" :option="option" />
        </template>
        <template #noResult>{{ $t('results_not_found') }}</template>
        <template #noOptions>{{ $t('results_not_found') }}</template>
        <template #afterList>
          <a class="text-center multiselect__element">
            <ScrollToEventElement v-if="canLoadMore && dbsearch && allowLoadOnScroll"
                                  @visible="loadOnScroll">
              &nbsp;
            </ScrollToEventElement>
          </a>
        </template>
      </VueMultiselect>
      <div class="d-flex flex-wrap gap-2 mt-2"
           v-if="resultsBelow && belowTags.length">
        <div class="service-multiselect-below-tag word-break"
             v-for="(o, oind) in belowTags"
             :key="`below-tag-${oind}`">
          {{ o[title] }}
          <a class="pointer font-size-0"
             v-if="!lastTagLocked"
             @click="removeBelow(o)">
            <CloseSvg class="svg-gray svg-size-sm"/>
          </a>
        </div>
      </div>
      <slot/>
      <ValidationError :class="[{ 'mt-1': !validationClass }, validationClass]"
                       v-if="showErrors"
                       :validationId="validationId"/>
    </div>
  </div>
</template>

<script>

import VueMultiselect from 'vue-multiselect'
import { EloquentService } from "@/services/api/eloquent.api.service"
import { mapState } from "vuex"
import ValidationError from "@/components/form/ValidationError.vue"
import ScrollToEventElement from "@/components/parts/general/ScrollToEventElement.vue"
import CloseSvg from "@/assets/svg-vue/general/close.svg"

export default {
  name: "ServiceMultiSelect",
  components: {
    ScrollToEventElement,
    ValidationError,
    VueMultiselect,
    CloseSvg
  },
  emits: ['select', 'remove', 'input'],
  props: {
    value: {},
    label: {},
    prepopulatedOptions: {},
    dbsearch: {
      type: Boolean,
      default: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: true
    },
    internalSearch: {
      type: Boolean,
      default: true
    },
    title: {
      type: String,
      default: 'name'
    },
    model: {
      type: String,
      default: 'service'
    },
    dblclick: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    ignoreIds: {
      type: Array,
      default() {
        return []
      }
    },
    placeholder: {
      type: String,
      default: 'search'
    },
    onlyId: {
      type: Boolean,
      default: false
    },
    valueId: {
      type: String,
      default: 'id'
    },
    validationId: {},
    validationClass: {
      type: String,
      default: null
    },
    showErrors: {
      type: Boolean,
      default: false
    },
    customOptionsLabel: {
      type: Function,
      default: null
    },
    customSingleLabel: {
      type: Function,
      default: null
    },
    autocompleteAdditionalParams: {
      type: Object,
      default: null
    },
    allowLoadOnScroll: {
      type: Boolean,
      default: false
    },
    perPage: {
      type: Number,
      default: 24
    },
    editMode: {
      type: Boolean,
      default: true
    },
    textLikeInput: {
      type: Boolean,
      default: false
    },
    formLabelClass: {
      type: String,
    },
    resultsBelow: {
      type: Boolean,
      default: false
    },
    allowEmpty: {
      type: Boolean,
      default: true
    },
  },
  computed: {
    ...mapState({
      errors: state => state.datatable.validationErrors,
      superadmin: state => state.auth.superadmin,
      clinic: state => state.auth.clinic,
      branch: state => state.auth.branch
    }),
    groupLabel() {
      return this.model === 'service' ? 'group-label' : ''
    },
    groupValues() {
      return this.model === 'service' ? 'group-values' : ''
    },
    hasErrors() {
      if(!this.showErrors || !this.validationId) return false
      return !!this.errors[this.validationId]
    },
    stringValue() {
      if(!this.f || this.f.length === 0) return '-'
      if(Array.isArray(this.f)) {
        return this.f.map(o => o[this.title]).join(', ')
      }
      return this.f[this.title]
    },
    belowTags() {
      if(!this.resultsBelow) return []
      return Array.isArray(this.f) ? this.f : (this.f ? [this.f] : [])
    },
    lastTagLocked() {
      return !this.allowEmpty && Array.isArray(this.f) && this.f.length <= 1
    }
  },
  data() {
    return {
      options: [],
      timer: null,
      f: [],
      searchString: null,
      canLoadMore: false,
      page: 1
    }
  },
  methods: {
    serviceFind(query, loadingMore = false) {
      this.searchString = query
      if(!this.dbsearch) return
      if(loadingMore) {
        this.page++
      } else {
        this.page = 1
      }
      if(this.timer) {
        clearTimeout(this.timer)
      }
      this.timer = setTimeout(() => {
        this.canLoadMore = false
        EloquentService.autocomplete(this.model,
            query,
            this.perPage,
            true,
            this.superadmin,
            this.superadmin && this.clinic ? this.clinic.id : null,
            this.branch,
            this.autocompleteAdditionalParams,
            this.page
        ).then(res => {
          let set = res.data.result
          if(this.ignoreIds && this.ignoreIds.length) {
            set = set.filter(x => x && (!x.id || !this.ignoreIds.includes(x.id)))
          }
          if(loadingMore) {
            this.options = [...this.options, ...set]
          } else {
            this.options = set
          }
          this.canLoadMore = set.length === this.perPage
        })
      }, 270)
    },
    tagClick(e) {
      let node = e.target
      if(node && node.parentNode && node.parentNode.classList.contains('multiselect__tag')) {
        node = node.parentNode
        let parent = node.parentNode
        let index = Array.from(parent.children).indexOf(node)
        this.f.push(this.f[index])
        this.$emit('select', this.f[index])
      }
    },
    remove(e) {
      this.$emit('remove', e)
    },
    select(e) {
      this.$emit('select', e)
    },
    openDropdown() {
      setTimeout(() => {
        this.$refs.multi.activate()
      }, 100)
    },
    loadOnScroll() {
      this.serviceFind(this.searchString, true)
    },
    removeBelow(option) {
      if(!this.allowEmpty && this.belowTags.length <= 1) return
      if(Array.isArray(this.f)) {
        this.f = this.f.filter(o => o[this.title] !== option[this.title])
      } else {
        this.f = null
      }
      this.$emit('remove', option)
    }
  },
  mounted() {
    this.f = this.value
    if(this.prepopulatedOptions) {
      this.options = this.prepopulatedOptions
      if(this.onlyId && this.value) {
        this.f = this.options.find(o => o[this.valueId].toString() === this.value.toString())
      }
    }
  },
  watch: {
    f(val) {
      if(this.onlyId) {
        this.$emit('input', val ? val[this.valueId] : '')
        return
      }
      this.$emit('input', val)
    },
    value(val) {
      this.f = val
      if(this.onlyId) {
        this.f = this.options.find(o => o[this.valueId].toString() === val.toString())
      }
    }
  },
  created() {
    if(this.dblclick) {
      setTimeout(() => {
        if(this.$refs.multiselect) {
          this.$refs.multiselect.addEventListener('dblclick', this.tagClick)
        }
      }, 20)
    }
  },
  beforeUnmount() {
    if(this.dblclick) {
      if(this.$refs.multiselect) {
        this.$refs.multiselect.removeEventListener('dblclick', this.tagClick)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.service-multiselect-below-tag {
  display: inline-flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  padding: 4px 8px;
  gap: 8px;
  font-size: 90%;
}

.multiselect {
  &.last-tag-locked :deep(.multiselect__tag-icon) {
    display: none;
  }

  :deep(.multiselect__content-wrapper) {
    overflow-x: hidden;
  }

  :deep(.multiselect__content .multiselect__element .multiselect__option) {
    white-space: normal;
    overflow-wrap: anywhere;
    line-height: 20px;
    padding-top: 5px;
    padding-bottom: 5px;
  }
}
</style>
